import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle2, Loader2 } from 'lucide-react';
import PhoneField, { isPhoneValid } from './PhoneField';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Lightweight enquiry modal pre-filled for an artist's "press pack / more info"
 * request. Mirrors the PriceRequestModal pattern but routes through
 * inquiry_type='artist_pack' so admin can triage these separately.
 */
const RequestArtistPackModal = ({ open, onClose, artist }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const reset = () => {
    setName(''); setEmail(''); setPhone(''); setNote('');
    setSubmitted(false); setError(''); setLoading(false);
  };
  const handleClose = () => { reset(); onClose(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isPhoneValid(phone)) {
      setError('Please enter a valid phone number with country code.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const message =
        `Request for press pack / further information about ${artist?.name || 'an artist'}.` +
        (phone ? ` Phone: ${phone}.` : '') +
        (note ? ` Notes: ${note}` : '');
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          email,
          phone,
          message,
          inquiry_type: 'artist_pack',
          artwork_title: artist?.name,
          artwork_id: artist?.id,
        }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || `Request failed (${res.status})`);
      }
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          onClick={handleClose}
          data-testid="artist-pack-modal-overlay"
        >
          <motion.div
            className="bg-background w-full max-w-md p-8 relative"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 hover:bg-muted/40 rounded transition-colors"
              data-testid="artist-pack-close-btn"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            {submitted ? (
              <div className="py-8 text-center" data-testid="artist-pack-submitted-state">
                <CheckCircle2 className="w-10 h-10 mx-auto text-foreground mb-4" />
                <h3 className="font-serif text-2xl mb-2">Request received</h3>
                <p className="font-sans text-sm text-muted-foreground">
                  Gabrielle and the team will send over the press pack for
                  {artist?.name ? ` ${artist.name}` : ''} within one working day.
                </p>
                <button
                  onClick={handleClose}
                  className="mt-6 px-6 py-3 border border-foreground text-foreground font-sans text-xs uppercase tracking-[0.2em]"
                  data-testid="artist-pack-done-btn"
                >
                  Close
                </button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-2">
                  Request artist pack
                </p>
                <h3 className="font-serif text-2xl mb-1">{artist?.name || 'Artist information'}</h3>
                <p className="font-sans text-sm text-muted-foreground mb-6">
                  Share your details and we'll send a curated PDF — works available, recent press,
                  pricing on request — within one working day.
                </p>

                <form onSubmit={handleSubmit} className="space-y-3" data-testid="artist-pack-form">
                  <input
                    required value={name} onChange={(e) => setName(e.target.value)}
                    placeholder="Full name"
                    className="w-full px-3 py-2 bg-transparent border border-border focus:border-foreground outline-none font-sans text-sm"
                    data-testid="artist-pack-name-input"
                  />
                  <input
                    required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="w-full px-3 py-2 bg-transparent border border-border focus:border-foreground outline-none font-sans text-sm"
                    data-testid="artist-pack-email-input"
                  />
                  <PhoneField
                    value={phone}
                    onChange={setPhone}
                    required
                    placeholder="Phone number"
                    testIdPrefix="artist-pack-phone"
                  />
                  <textarea
                    rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder={`Any specific works or budgets? (optional)`}
                    className="w-full px-3 py-2 bg-transparent border border-border focus:border-foreground outline-none font-sans text-sm resize-none"
                    data-testid="artist-pack-notes-input"
                  />
                  {error && <p className="text-xs text-red-600 font-sans">{error}</p>}
                  <button
                    type="submit" disabled={loading}
                    className="w-full px-6 py-3 bg-foreground text-background font-sans text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2 disabled:opacity-60"
                    data-testid="artist-pack-submit-btn"
                  >
                    {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending</> : 'Send request'}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RequestArtistPackModal;
